import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";

// Contexts
import { usePayPlan } from "@/PayPlans/context/PayPlans";

// Layouts
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import SingleTiersLoader from "@/Layouts/components/Skeletons/SingleTiersLoader";

// Custom components
import MiniDealContainer from "./MiniDealContainer";

// MUI
import { Grid } from "@mui/material";

export default function MiniDeal(props) {
	const {
		sectionId = "miniDeal",
		brands = {
			new: [],
			preOwned: [],
		},
		tabsProps = {
			tabs: [],
			errors: [],
		},
		miniDeal = null,
		loading = false,
		loadingMakes = false,
		readOnly = false,
		onActionUpdate = () => {},
	} = props;

	// Hooks
	const { useSectionsErrors } = usePayPlan();
	const { getErrorKey, addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors();

	// States
	const [model, setModel] = useState(null);

	// Error handler
	const errors = useMemo(() => {
		const errorNew = tabsProps.errors?.includes(getErrorKey(sectionId, "new"));
		const errorPreOwned = tabsProps.errors?.includes(getErrorKey(sectionId, "preOwned"));
		return [errorNew, errorPreOwned];
	}, [sectionId, tabsProps.errors, getErrorKey]);

	// Actions handlers
	const handleUpdate = (section, area, data) => {
		const deals = { ...model?.miniDeal, [area]: data };
		const update = { ...model, deals };

		setModel(update);
		onActionUpdate?.(section, area, data);
	};

	const handleValidation = (section, area, value) => {
		value ? removeSectionError(section, area) : addSectionError(section, area);
	};

	React.useEffect(() => {
		setModel({
			tabsProps,
			miniDeal,
		});

		return () => {
			setModel(null);
		};
	}, [tabsProps, miniDeal]);

	React.useEffect(() => {
		return () => {
			clearSectionErrors(sectionId);
		};
	}, [sectionId, clearSectionErrors]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid>
						<TabLoader />
						<SingleTiersLoader buttons selector />
					</Grid>
				) : (
					!!model?.miniDeal && (
						<TabSelectable
							tabs={model?.tabsProps.tabs || []}
							errors={errors}
							content={Object.entries(model.miniDeal)?.map(([key, value], index) => (
								<Grid container key={`${key}_${index}`}>
									<MiniDealContainer
										tab={key}
										miniDeals={value}
										brands={brands?.[key] || []}
										loading={loadingMakes}
										readOnly={readOnly}
										onActionUpdate={(updatedModel) => handleUpdate(sectionId, key, updatedModel)}
										onActionValidation={(updatedValidation) => handleValidation(sectionId, key, updatedValidation)}
									/>
								</Grid>
							))}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
MiniDeal.propTypes = {
	sectionId: PropTypes.string,
	brands: PropTypes.shape({
		new: PropTypes.array,
		preOwned: PropTypes.array,
	}),
	tabsProps: PropTypes.shape({
		tabs: PropTypes.array,
		errors: PropTypes.array,
	}),
	miniDeal: PropTypes.object,
	loading: PropTypes.bool,
	loadingMakes: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
};
